import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
const { FiLock, FiMail, FiEye, FiEyeOff, FiCheck, FiHelpCircle, FiLoader, FiAlertCircle, FiArrowRight, FiShield } = FiIcons;
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPasscode, setShowPasscode] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (user) {
      const origin = location.state?.from?.pathname || '/leads';
      navigate(origin, { replace: true });
    }
  }, [user, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    const newErrors = {};
    if (!email) newErrors.email = 'Email address is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Please enter a valid email';
    if (!passcode) newErrors.passcode = 'Passcode is required';
    else if (passcode.length !== 6) newErrors.passcode = 'Passcode must be 6 digits';
    
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const response = await authApi.verifyLogin(email, passcode);
      if (response.success && response.client) {
        login(response.client, rememberMe);
      } else {
        setApiError(response.message || 'Invalid credentials. Please check your email and passcode.');
      }
    } catch (err) {
      setApiError('Unable to connect to the portal. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-grow flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 bg-white/5 backdrop-blur-md rounded-[2rem] sm:rounded-[3rem] overflow-hidden shadow-2xl border border-white/10">
        {/* Left Side: Hero / Branding */}
        <div className="hidden md:flex flex-col justify-between p-10 lg:p-14 bg-navy-800 to-navy-900 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
            <div className="absolute top-[-10%] right-[-10%] w-64 h-64 bg-brand-cyan rounded-full blur-[120px]"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-64 h-64 bg-brand-blue rounded-full blur-[120px]"></div>
          </div>
          <div className="relative z-10">
            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-lg mb-10 transform -rotate-6">
              <span className="text-navy-900 font-black text-2xl tracking-tighter">BPA</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight leading-[1.1]">
              Client <br />
              <span className="text-brand-cyan">Lead Portal</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-xs leading-relaxed font-medium">
              A premium, secure environment to manage your business enquiries.
            </p>
          </div>
          <div className="relative z-10 mt-10">
            <div className="flex items-center gap-4 p-5 bg-white/5 border border-white/10 rounded-[1.5rem] backdrop-blur-sm">
              <div className="w-12 h-12 rounded-full bg-brand-blue/20 flex items-center justify-center text-brand-cyan shadow-inner">
                <SafeIcon icon={FiHelpCircle} className="text-xl" />
              </div>
              <div className="text-xs">
                <p className="text-white font-black uppercase tracking-widest mb-0.5">Assistance</p>
                <p className="text-slate-400 font-medium">Contact BPA Solutions Support</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Login Form */}
        <div className="bg-white p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
          <div className="md:hidden text-center mb-10">
            <div className="w-12 h-12 bg-navy-900 rounded-xl flex items-center justify-center shadow-lg mx-auto mb-4">
              <span className="text-white font-bold text-lg">BPA</span>
            </div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">BPAS Client Lead Portal</h1>
          </div>

          <div className="mb-10 text-center sm:text-left">
            <h2 className="text-3xl font-black text-slate-900 mb-1 tracking-tight">BPAS Client Lead Portal</h2>
            <p className="text-slate-500 font-bold text-xs uppercase tracking-widest">Client access only</p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <AnimatePresence mode="wait">
              {apiError && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  exit={{ opacity: 0, y: -10 }} 
                  className="bg-red-50 border border-red-100 rounded-2xl p-4 flex flex-col gap-2"
                >
                  <div className="flex gap-3 text-red-600 text-sm font-bold">
                    <SafeIcon icon={FiAlertCircle} className="text-xl flex-shrink-0" />
                    <p>{apiError}</p>
                  </div>
                  <p className="text-[10px] text-red-400 font-medium leading-relaxed italic ml-8">
                    Client access uses the email and 6-digit code configured for this business in the Clients tab. BPAS admins should use the Admin Console.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-2">
              <label className="block text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Business Email</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-brand-blue">
                  <SafeIcon icon={FiMail} className={`${errors.email ? 'text-red-400' : 'text-slate-400'}`} />
                </div>
                <input 
                  type="email" value={email} 
                  onChange={(e) => { setEmail(e.target.value); if(errors.email) setErrors({...errors, email: null}); }} 
                  disabled={isSubmitting}
                  placeholder="e.g. john@business.com" 
                  className={`block w-full pl-12 pr-4 py-4 bg-slate-50 border-2 ${errors.email ? 'border-red-200 ring-red-50' : 'border-transparent focus:border-brand-blue/20 focus:ring-4 focus:ring-brand-blue/5'} rounded-2xl text-slate-900 placeholder-slate-400 transition-all outline-none font-semibold`} 
                />
              </div>
              {errors.email && <p className="text-[10px] font-bold text-red-500 ml-1 uppercase tracking-tighter">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <label className="block text-xs font-black text-slate-400 uppercase tracking-widest ml-1">6-Digit Passcode</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-brand-blue">
                  <SafeIcon icon={FiLock} className={`${errors.passcode ? 'text-red-400' : 'text-slate-400'}`} />
                </div>
                <input 
                  type={showPasscode ? "text" : "password"} 
                  value={passcode} 
                  onChange={(e) => { const val = e.target.value.replace(/\D/g, '').slice(0, 6); setPasscode(val); if(errors.passcode) setErrors({...errors, passcode: null}); }} 
                  disabled={isSubmitting}
                  placeholder="••••••" 
                  className={`block w-full pl-12 pr-12 py-4 bg-slate-50 border-2 ${errors.passcode ? 'border-red-200 ring-red-50' : 'border-transparent focus:border-brand-blue/20 focus:ring-4 focus:ring-brand-blue/5'} rounded-2xl text-slate-900 placeholder-slate-300 transition-all outline-none tracking-[0.4em] font-mono text-lg font-bold`} 
                />
                <button type="button" onClick={() => setShowPasscode(!showPasscode)} className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-brand-blue transition-colors">
                  <SafeIcon icon={showPasscode ? FiEyeOff : FiEye} />
                </button>
              </div>
              {errors.passcode && <p className="text-[10px] font-bold text-red-500 ml-1 uppercase tracking-tighter">{errors.passcode}</p>}
            </div>

            <div className="flex items-center justify-between ml-1 pb-2">
              <label className="flex items-center group cursor-pointer">
                <input type="checkbox" checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} className="sr-only" disabled={isSubmitting} />
                <div className={`w-5 h-5 border-2 rounded-lg transition-all duration-300 flex items-center justify-center ${rememberMe ? 'bg-brand-blue border-brand-blue shadow-lg shadow-brand-blue/30' : 'bg-white border-slate-200 group-hover:border-brand-blue'}`}>
                  {rememberMe && <SafeIcon icon={FiCheck} className="text-white text-[10px]" />}
                </div>
                <span className="ml-3 text-sm font-bold text-slate-500 group-hover:text-slate-900 transition-colors">Remember this device</span>
              </label>
            </div>

            <button type="submit" disabled={isSubmitting} className="w-full relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-brand-blue to-brand-cyan rounded-2xl blur-lg opacity-40 group-hover:opacity-70 transition-opacity"></div>
              <div className="relative flex items-center justify-center gap-3 px-8 py-5 bg-navy-900 text-white font-black uppercase tracking-widest text-xs rounded-2xl transition-all group-hover:-translate-y-1 active:translate-y-0 shadow-2xl disabled:opacity-70">
                {isSubmitting ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin text-lg" />
                    <span>Authorizing...</span>
                  </>
                ) : (
                  <>
                    <span>Verify & Access</span>
                    <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </div>
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-slate-100 flex justify-center">
            <Link 
              to="/admin/login" 
              className="group flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-brand-blue transition-colors"
            >
              <SafeIcon icon={FiShield} className="text-xs" />
              <span>BPAS admin? <span className="underline underline-offset-4 group-hover:no-underline">Open admin console</span></span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}